import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const RetentionActivityChart = ({ data, timeRange, isLoading, dataMode = 'users' }) => {
  const styles = {
    primaryBg: "#ffffff",
    textPrimary: "#0f172a", 
    textSecondary: "#666666",
    accentColor: "#1d0c46"
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    switch (timeRange) {
      case 'weekly':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'yearly':
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      case 'monthly':
      default:
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg">
          <p className="text-sm font-medium mb-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
            {formatDate(label)}
          </p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color, fontFamily: 'Raleway, sans-serif' }}>
              {entry.name}: {formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderColor: styles.accentColor }}></div>
          <p className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading chart...
          </p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No data available
          </p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis 
          dataKey="date" 
          tickFormatter={formatDate}
          stroke={styles.textSecondary}
          fontSize={11}
          fontFamily="Raleway, sans-serif"
        />
        <YAxis 
          tickFormatter={formatNumber}
          stroke={styles.textSecondary}
          fontSize={11}
          fontFamily="Raleway, sans-serif"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px' }}
          iconType="line"
        />
        {dataMode === 'wallets' ? (
          <>
            <Line 
              type="monotone" 
              dataKey="daw" 
              stroke="#1d0c46" 
              strokeWidth={2}
              dot={false}
              name="DAW"
            />
            <Line 
              type="monotone" 
              dataKey="waw" 
              stroke="#4c1d95" 
              strokeWidth={2}
              dot={false}
              name="WAW"
            />
            <Line 
              type="monotone" 
              dataKey="maw" 
              stroke="#7c3aed" 
              strokeWidth={2}
              dot={false}
              name="MAW"
            />
          </>
        ) : (
          <>
            <Line 
              type="monotone" 
              dataKey="dau" 
              stroke="#1d0c46" 
              strokeWidth={2}
              dot={false}
              name="DAU"
            />
            <Line 
              type="monotone" 
              dataKey="wau" 
              stroke="#4c1d95" 
              strokeWidth={2}
              dot={false}
              name="WAU"
            />
            <Line 
              type="monotone" 
              dataKey="mau" 
              stroke="#7c3aed" 
              strokeWidth={2}
              dot={false}
              name="MAU"
            />
          </>
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default RetentionActivityChart;
